import type { ReactNode } from 'react';

import styles from './CriticalStrip.module.css';

export interface CriticalStripProps {
  /** Clinical danger (a critical result): danger rule and text, announced as an alert. */
  danger?: boolean;
  /** One sentence: what happened and for whom. */
  children: ReactNode;
  /**
   * The only way the strip goes away, e.g. "Open and acknowledge". A passive notice (one
   * that goes away by itself when its cause does) has none.
   */
  action?: ReactNode;
  /** An accessible name for the strip, e.g. "Your licence". */
  label?: string;
}

/**
 * A full-width strip under the top bar for a clinical notification (lab plan §6.3). It has
 * no close button: a critical result stays on every screen until the person opens and
 * acknowledges it. Notices that need no acknowledgement use the quiet ink style.
 */
export function CriticalStrip({ danger = false, children, action, label }: CriticalStripProps) {
  return (
    <div
      role={danger ? 'alert' : 'status'}
      aria-label={label}
      className={styles.strip}
      data-danger={danger || undefined}
    >
      <p className={styles.message}>{children}</p>
      {action === undefined ? null : <div className={styles.action}>{action}</div>}
    </div>
  );
}
